// Mercapoli - Landing Page Dinámica (Fase 5)
// Geolocalización + Comercios por Ciudad + SEO Dinámico
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  MapPin, Clock, Star, ChevronRight, Smartphone, 
  Store, Bike, Shield, ArrowRight, Menu, X, Loader2,
  Navigation, ChevronLeft, Heart, Search, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useCity } from '../context/CityContext';
import { restaurantes as restaurantesApi, categorias as categoriasApi, ciudades as ciudadesApi } from '../lib/api';
import { toast } from 'sonner';
import CitySelector from '../components/CitySelector';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mercadelivery-1/artifacts/5y038jeg_icono_mercapoli.png";

const Landing = () => {
  const navigate = useNavigate();
  const { ciudadSlug } = useParams(); // Para rutas /ciudad/:slug
  const { isAuthenticated, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { ciudad, setCiudad, ciudades: ciudadesContext } = useCity();
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [geoLoading, setGeoLoading] = useState(false);
  const [showGeoPrompt, setShowGeoPrompt] = useState(false);
  const [detectedCity, setDetectedCity] = useState(null);
  
  // Datos dinámicos
  const [comercios, setComercios] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [comerciosPorCategoria, setComerciosPorCategoria] = useState({});

  // Redirigir si ya está autenticado
  useEffect(() => {
    if (isAuthenticated && user) {
      const redirectMap = {
        customer: '/app',
        delivery: '/rider',
        restaurant: '/merchant',
        admin: '/admin'
      };
      navigate(redirectMap[user.rol] || '/app');
    }
  }, [isAuthenticated, user, navigate]);

  // Intentar geolocalización al cargar
  useEffect(() => {
    if (!ciudad && !ciudadSlug) {
      tryGeolocation();
    }
  }, []);

  // Cargar datos cuando hay ciudad
  useEffect(() => {
    if (ciudad?.ciudad_id) {
      loadCityData();
    }
  }, [ciudad]);

  // Manejar ruta /ciudad/:slug
  useEffect(() => {
    if (ciudadSlug && ciudadesContext.length > 0) {
      const foundCity = ciudadesContext.find(c => 
        c.slug === ciudadSlug || 
        c.nombre.toLowerCase().replace(/\s+/g, '-') === ciudadSlug.toLowerCase()
      );
      if (foundCity && foundCity.ciudad_id !== ciudad?.ciudad_id) {
        setCiudad(foundCity);
      }
    }
  }, [ciudadSlug, ciudadesContext]);

  const tryGeolocation = async () => {
    if (!navigator.geolocation) {
      setLoading(false);
      return;
    }

    setGeoLoading(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          // Buscar ciudad más cercana
          const { latitude, longitude } = position.coords;
          const nearestCity = await findNearestCity(latitude, longitude);
          
          if (nearestCity) {
            setDetectedCity(nearestCity);
            setShowGeoPrompt(true);
          }
        } catch (error) {
          console.error('Error finding nearest city:', error);
        }
        setGeoLoading(false);
        setLoading(false);
      },
      (error) => {
        console.log('Geolocation denied or unavailable');
        setGeoLoading(false);
        setLoading(false);
      },
      { timeout: 5000, enableHighAccuracy: false }
    );
  };

  const findNearestCity = async (lat, lng) => {
    // Aproximación simple: usar ciudades cargadas y comparar
    // En producción esto sería una API más sofisticada
    if (ciudadesContext.length > 0) {
      // Por ahora, retornar la primera ciudad de Guatemala si está cerca
      // En el futuro, calcular distancia real
      const gtCities = ciudadesContext.filter(c => c.pais === 'GT');
      return gtCities[0] || ciudadesContext[0];
    }
    return null;
  };

  const confirmDetectedCity = () => {
    if (detectedCity) {
      setCiudad(detectedCity);
      setShowGeoPrompt(false);
      toast.success(`¡Ubicación confirmada! Mostrando comercios en ${detectedCity.nombre}`);
    }
  };

  const loadCityData = async () => {
    setLoading(true);
    try {
      // Cargar comercios de la ciudad
      const [comerciosData, categoriasData] = await Promise.all([
        restaurantesApi.getAll({ ciudad_id: ciudad.ciudad_id, activo: true }),
        categoriasApi.getAll()
      ]);

      setComercios(comerciosData || []);
      setCategorias(categoriasData || []);

      // Agrupar comercios por categoría
      const grouped = {};
      (comerciosData || []).forEach(comercio => {
        const catId = comercio.categoria_id || 'otros';
        if (!grouped[catId]) {
          grouped[catId] = [];
        }
        grouped[catId].push(comercio);
      });
      setComerciosPorCategoria(grouped);

    } catch (error) {
      console.error('Error loading city data:', error);
    }
    setLoading(false);
  };

  // SEO Meta tags dinámicos
  const getSEOData = () => {
    if (ciudad) {
      return {
        title: `Delivery en ${ciudad.nombre} | Mercapoli - Pide comida a domicilio`,
        description: `Pide comida a domicilio en ${ciudad.nombre}. Restaurantes, farmacias, minimarkets y más. Entrega rápida con Mercapoli.`,
        url: `https://mercapoliapp.com/ciudad/${ciudad.slug || ciudad.nombre.toLowerCase().replace(/\s+/g, '-')}`
      };
    }
    return {
      title: 'Mercapoli - Delivery de comida y más en Latinoamérica',
      description: 'La mejor app de delivery en Latinoamérica. Pide comida, farmacia, minimarket y más. Entrega rápida y segura.',
      url: 'https://mercapoliapp.com'
    };
  };

  const seo = getSEOData();

  const stats = [
    { value: "500+", label: "Comercios" },
    { value: "50K+", label: "Pedidos entregados" },
    { value: "10K+", label: "Usuarios felices" },
    { value: "4.9", label: "Calificación promedio" }
  ];

  const features = [
    {
      icon: <Smartphone className="h-8 w-8" />,
      title: "Pide desde cualquier lugar",
      description: "Explora cientos de comercios y pide tus productos favoritos con unos pocos toques"
    },
    {
      icon: <Clock className="h-8 w-8" />,
      title: "Entrega rápida",
      description: "Recibe tu pedido en minutos con nuestro sistema de Riders optimizado"
    },
    {
      icon: <MapPin className="h-8 w-8" />,
      title: "Seguimiento en tiempo real",
      description: "Sigue tu pedido en el mapa desde el comercio hasta tu puerta"
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Pagos seguros",
      description: "Múltiples opciones de pago con la máxima seguridad"
    }
  ];

  // Tipos de comercio para mostrar
  const tiposComercio = [
    { name: "Restaurantes", emoji: "🍽️", color: "#FF6B35", slug: "restaurantes" },
    { name: "Farmacias", emoji: "💊", color: "#00BFA6", slug: "farmacias" },
    { name: "Minimarkets", emoji: "🏪", color: "#4A90D9", slug: "minimarkets" },
    { name: "Mercados", emoji: "🥬", color: "#7CB342", slug: "mercados" },
    { name: "Floristerías", emoji: "💐", color: "#E91E63", slug: "floristerias" },
    { name: "Panaderías", emoji: "🥐", color: "#FF8F00", slug: "panaderias" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={seo.url} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
      </Helmet>

      {/* Geolocation Prompt Modal */}
      <AnimatePresence>
        {showGeoPrompt && detectedCity && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card rounded-2xl p-6 max-w-md w-full shadow-xl"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-brand-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Navigation className="h-8 w-8 text-brand-primary" />
                </div>
                <h3 className="text-xl font-bold mb-2">¡Te encontramos!</h3>
                <p className="text-muted-foreground mb-4">
                  Parece que estás en <strong>{detectedCity.nombre}</strong>. 
                  ¿Es correcto?
                </p>
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowGeoPrompt(false)}
                  >
                    Cambiar ciudad
                  </Button>
                  <Button 
                    className="flex-1 bg-brand-primary hover:bg-brand-primary/90"
                    onClick={confirmDetectedCity}
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    Sí, confirmar
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Mercapoli" className="w-10 h-10" />
              <span className="font-heading text-xl font-bold">Mercapoli</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              <CitySelector />
              <a href="#comercios" className="text-muted-foreground hover:text-foreground transition-colors">
                Comercios
              </a>
              <a href="#categorias" className="text-muted-foreground hover:text-foreground transition-colors">
                Categorías
              </a>
              <a href="#partners" className="text-muted-foreground hover:text-foreground transition-colors">
                Socios
              </a>
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-4">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
                aria-label="Cambiar tema"
              >
                {theme === 'dark' ? '🌞' : '🌙'}
              </button>
              
              <div className="hidden md:flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" data-testid="login-btn">
                    Iniciar sesión
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-brand-primary hover:bg-brand-primary/90" data-testid="register-btn">
                    Registrarse
                  </Button>
                </Link>
              </div>

              {/* Mobile menu button */}
              <button
                className="md:hidden p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X /> : <Menu />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden border-t border-border bg-background p-4"
          >
            <nav className="flex flex-col gap-4">
              <div className="py-2">
                <CitySelector />
              </div>
              <a href="#comercios" className="py-2">Comercios</a>
              <a href="#categorias" className="py-2">Categorías</a>
              <a href="#partners" className="py-2">Socios</a>
              <hr className="border-border" />
              <Link to="/login">
                <Button variant="outline" className="w-full">Iniciar sesión</Button>
              </Link>
              <Link to="/register">
                <Button className="w-full bg-brand-primary">Registrarse</Button>
              </Link>
            </nav>
          </motion.div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 overflow-hidden">
        <div className="container-app">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {ciudad && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 mb-4"
                >
                  <Badge variant="secondary" className="text-sm px-3 py-1">
                    <MapPin className="h-3 w-3 mr-1" />
                    {ciudad.nombre}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {comercios.length} comercios disponibles
                  </span>
                </motion.div>
              )}
              
              <span className="inline-block px-4 py-2 rounded-full bg-brand-primary/10 text-brand-primary text-sm font-medium mb-6">
                🚀 La mejor app de delivery en LatAm
              </span>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                {ciudad ? (
                  <>
                    Delivery en
                    <span className="text-brand-primary"> {ciudad.nombre}</span>
                  </>
                ) : (
                  <>
                    Tu comida favorita,
                    <span className="text-brand-primary"> a tu puerta</span>
                  </>
                )}
              </h1>
              
              <p className="text-lg text-muted-foreground mb-8 max-w-lg">
                {ciudad 
                  ? `Descubre los mejores comercios de ${ciudad.nombre}. Restaurantes, farmacias, minimarkets y más con entrega rápida.`
                  : 'Descubre cientos de restaurantes cerca de ti. Pide fácil, paga seguro y recibe tu comida en minutos.'
                }
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="bg-brand-primary hover:bg-brand-primary/90 text-white rounded-full px-8 py-6 text-lg shadow-glow btn-press"
                    data-testid="hero-cta"
                  >
                    Comenzar ahora
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                {!ciudad && (
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="rounded-full px-8 py-6 text-lg"
                    onClick={tryGeolocation}
                    disabled={geoLoading}
                  >
                    {geoLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    ) : (
                      <Navigation className="h-5 w-5 mr-2" />
                    )}
                    Usar mi ubicación
                  </Button>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4 mt-12">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="font-heading text-2xl font-bold text-brand-primary">
                      {stat.value}
                    </div>
                    <div className="text-xs text-muted-foreground">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=600"
                  alt="Comida deliciosa"
                  className="rounded-3xl shadow-2xl w-full"
                />
                
                {/* Floating cards */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="absolute -bottom-6 -left-6 glass rounded-2xl p-4 shadow-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-brand-accent/20 flex items-center justify-center">
                      <Clock className="h-6 w-6 text-brand-accent" />
                    </div>
                    <div>
                      <p className="font-bold">15-30 min</p>
                      <p className="text-sm text-muted-foreground">Tiempo de entrega</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="absolute -top-4 -right-4 glass rounded-2xl p-4 shadow-xl"
                >
                  <div className="flex items-center gap-2">
                    <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    <span className="font-bold">4.9</span>
                    <span className="text-sm text-muted-foreground">(2.5k reseñas)</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tipos de Comercio - Horizontal Scroll */}
      <section className="py-8 border-y border-border/50 bg-muted/30">
        <div className="container-app">
          <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-hide">
            {tiposComercio.map((tipo, index) => (
              <motion.button
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border hover:border-brand-primary hover:bg-brand-primary/5 transition-all whitespace-nowrap"
                onClick={() => navigate(`/app?categoria=${tipo.slug}`)}
              >
                <span className="text-xl">{tipo.emoji}</span>
                <span className="font-medium">{tipo.name}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Comercios por Categoría - Dinámico */}
      {ciudad && comercios.length > 0 && (
        <section id="comercios" className="py-16">
          <div className="container-app">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold">
                  Comercios en {ciudad.nombre}
                </h2>
                <p className="text-muted-foreground mt-1">
                  {comercios.length} opciones para ti
                </p>
              </div>
              <Link to="/app">
                <Button variant="outline">
                  Ver todos
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>

            {/* Carruseles por categoría */}
            {categorias.filter(cat => comerciosPorCategoria[cat.categoria_id]?.length > 0).map((categoria, catIdx) => (
              <div key={categoria.categoria_id} className="mb-10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                    <span>{categoria.icono || '🏪'}</span>
                    {categoria.nombre}
                    <Badge variant="secondary" className="ml-2">
                      {comerciosPorCategoria[categoria.categoria_id]?.length}
                    </Badge>
                  </h3>
                  <Button variant="ghost" size="sm">
                    Ver más <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
                
                <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                  {comerciosPorCategoria[categoria.categoria_id]?.slice(0, 6).map((comercio, idx) => (
                    <motion.div
                      key={comercio.restaurante_id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex-shrink-0 w-72"
                    >
                      <Link to={`/app/restaurante/${comercio.restaurante_id}`}>
                        <Card className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group">
                          <div className="relative h-40 overflow-hidden">
                            <img 
                              src={comercio.banner || comercio.imagen || 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400'}
                              alt={comercio.nombre}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            {comercio.promocion && (
                              <Badge className="absolute top-2 left-2 bg-brand-accent">
                                {comercio.promocion}
                              </Badge>
                            )}
                            <button className="absolute top-2 right-2 p-2 rounded-full bg-white/80 hover:bg-white transition-colors">
                              <Heart className="h-4 w-4" />
                            </button>
                          </div>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-bold truncate">{comercio.nombre}</h4>
                                <p className="text-sm text-muted-foreground truncate">
                                  {comercio.descripcion || comercio.direccion}
                                </p>
                              </div>
                              {comercio.logo && (
                                <img 
                                  src={comercio.logo} 
                                  alt="" 
                                  className="w-10 h-10 rounded-lg object-cover border"
                                />
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-3 text-sm">
                              <span className="flex items-center gap-1">
                                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                {comercio.calificacion || '4.5'}
                              </span>
                              <span className="text-muted-foreground">•</span>
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <Clock className="h-4 w-4" />
                                {comercio.tiempo_entrega_estimado || '30-45 min'}
                              </span>
                              {comercio.costo_envio > 0 && (
                                <>
                                  <span className="text-muted-foreground">•</span>
                                  <span className="text-muted-foreground">
                                    Envío Q{comercio.costo_envio}
                                  </span>
                                </>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}

            {/* Si no hay categorías con comercios, mostrar todos */}
            {Object.keys(comerciosPorCategoria).length === 0 && comercios.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {comercios.slice(0, 8).map((comercio, idx) => (
                  <motion.div
                    key={comercio.restaurante_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Link to={`/app/restaurante/${comercio.restaurante_id}`}>
                      <Card className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer">
                        <div className="relative h-40">
                          <img 
                            src={comercio.banner || 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400'}
                            alt={comercio.nombre}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <CardContent className="p-4">
                          <h4 className="font-bold">{comercio.nombre}</h4>
                          <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span>{comercio.calificacion || '4.5'}</span>
                            <span>•</span>
                            <span>{comercio.tiempo_entrega_estimado || '30-45 min'}</span>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Features Section */}
      <section id="features" className="py-20 bg-muted/30">
        <div className="container-app">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              ¿Por qué Mercapoli?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              La forma más fácil y segura de pedir todo lo que necesitas
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full text-center p-6 hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 rounded-2xl bg-brand-primary/10 flex items-center justify-center mx-auto mb-4 text-brand-primary">
                    {feature.icon}
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Partners Section */}
      <section id="partners" className="py-20">
        <div className="container-app">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Comercios */}
            <Card className="p-8 bg-gradient-to-br from-brand-primary/10 to-brand-primary/5 border-brand-primary/20">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-brand-primary/20 flex items-center justify-center">
                  <Store className="h-7 w-7 text-brand-primary" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold">¿Tienes un negocio?</h3>
                  <p className="text-muted-foreground">Únete como comercio asociado</p>
                </div>
              </div>
              <p className="text-muted-foreground mb-6">
                Aumenta tus ventas llegando a miles de clientes. Sin cuota de inscripción, solo pagas por pedido.
              </p>
              <Link to="/registro-comercio">
                <Button className="bg-brand-primary hover:bg-brand-primary/90">
                  Registrar mi negocio
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </Card>

            {/* Riders */}
            <Card className="p-8 bg-gradient-to-br from-brand-accent/10 to-brand-accent/5 border-brand-accent/20">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-brand-accent/20 flex items-center justify-center">
                  <Bike className="h-7 w-7 text-brand-accent" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold">¿Quieres ser Rider?</h3>
                  <p className="text-muted-foreground">Gana dinero con tu vehículo</p>
                </div>
              </div>
              <p className="text-muted-foreground mb-6">
                Trabaja cuando quieras, gana lo que necesitas. Únete a nuestra flota de repartidores.
              </p>
              <Link to="/registro-rider">
                <Button variant="outline" className="border-brand-accent text-brand-accent hover:bg-brand-accent/10">
                  Quiero ser Rider
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border bg-muted/30">
        <div className="container-app">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src={LOGO_URL} alt="Mercapoli" className="w-8 h-8" />
                <span className="font-heading font-bold">Mercapoli</span>
              </div>
              <p className="text-sm text-muted-foreground">
                La mejor app de delivery en Latinoamérica
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Empresa</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/sobre-nosotros" className="hover:text-foreground">Sobre nosotros</Link></li>
                <li><Link to="/trabaja-con-nosotros" className="hover:text-foreground">Trabaja con nosotros</Link></li>
                <li><Link to="/blog" className="hover:text-foreground">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/terminos-y-condiciones" className="hover:text-foreground">Términos y condiciones</Link></li>
                <li><Link to="/politica-de-privacidad" className="hover:text-foreground">Política de privacidad</Link></li>
                <li><Link to="/politica-de-cookies" className="hover:text-foreground">Cookies</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Soporte</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="mailto:soporte@mercapoliapp.com" className="hover:text-foreground">soporte@mercapoliapp.com</a></li>
                <li><a href="tel:+50212345678" className="hover:text-foreground">+502 1234 5678</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-border text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} Mercapoli. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
